import { useAuth } from '../../auth/authContext.js';
import { useMaintenanceStatus } from '../../features/maintenance/api.js';

const DEFAULT_MESSAGE =
  'Catalyst PSA is undergoing maintenance. Changes are temporarily disabled — please check back shortly.';

/**
 * Surfaces maintenance mode to the user:
 *  - Non-admins get a full-screen, blocking overlay (the API also rejects their writes).
 *  - Admins get a non-blocking amber banner so they can keep operating and turn the flag
 *    back off; their writes are still permitted by the API.
 * Renders nothing when maintenance is off.
 */
export function MaintenanceNotice(): JSX.Element | null {
  const { isAdmin, impersonator } = useAuth();
  const { data } = useMaintenanceStatus();

  if (!data?.enabled) {
    return null;
  }

  const message = data.message?.trim() || DEFAULT_MESSAGE;

  // Treat an admin who is impersonating a member as an admin: their `isAdmin` reflects the
  // impersonated (non-admin) member, but the real admin is still in control. Only admins
  // can impersonate, so the presence of `impersonator` means an admin is behind the
  // session. Showing them the blocking overlay would hide the "Stop impersonating" banner
  // and trap them.
  const actsAsAdmin = isAdmin || impersonator !== null;

  if (actsAsAdmin) {
    return (
      <div className="flex flex-wrap items-center justify-center gap-2 bg-amber-500 px-4 py-2 text-sm font-medium text-amber-950">
        <span>
          Maintenance mode is <strong>ON</strong>. Only administrators can make changes.
        </span>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 p-4 dark:bg-black/70">
      <div className="w-full max-w-md rounded-xl border border-amber-200 bg-white p-8 text-center shadow-xl">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            className="h-6 w-6 text-amber-600"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v3.75m0 3.75h.008M10.34 3.34 1.6 18a1.5 1.5 0 0 0 1.3 2.25h18.2a1.5 1.5 0 0 0 1.3-2.25L13.66 3.34a1.5 1.5 0 0 0-2.6 0Z"
            />
          </svg>
        </div>
        <h2 className="mt-4 text-lg font-semibold text-slate-900">We&apos;ll be right back</h2>
        <p className="mt-2 text-sm text-slate-600">{message}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-6 inline-flex items-center justify-center rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          Check again
        </button>
      </div>
    </div>
  );
}
